<template>
    <div class="flex gap-6 filters-view-outer w-full">
        <LibyanOrnament vertical/>
        <div class="filters-view w-full">
            <h2 class="page-header">{{ $t('message.filters.header') }}</h2>
            <div class="studbook-generator flex gap-2 justify-content-center mb-2 flex-wrap">
                <CustomInputNumber name="volume" :label="$t('message.filters.inputs.volume')"
                                   v-model="studbook.volume" noPlaceholder
                                   :customErrorMessage="getRequiredMessage(studbook.volume)"/>
                <CustomCalendar name="yearFrom" :label="$t('message.filters.inputs.yearFrom')"
                                view="year" noPlaceholder v-model="studbook.yearFrom"
                                dateFormat="yy"
                                :customErrorMessage="getRequiredMessage(studbook.yearFrom)"/>
                <CustomCalendar name="yearTo" :label="$t('message.filters.inputs.yearTo')"
                                view="year" noPlaceholder v-model="studbook.yearTo" dateFormat="yy"
                                :customErrorMessage="getErrorMsg(studbook.yearTo)"/>
                <CustomButton
                    :label="$t('message.filters.generateStudbook')"
                    @click="generateStudbook"
                    class="ml-4"
                    :disabled="!!taskStatus && (taskStatus.status === 'PENDING' || taskStatus.status === 'IN_PROGRESS')"/>

                <div v-if="taskStatus || previousCompletedTask"
                     class="task-status-container flex align-items-center gap-3 ml-3 flex-wrap flex-column">

                    <div class="flex gap-3 mt-3 align-items-center" v-if="taskStatus">
                        <div class="flex align-items-center gap-2">
                            <i v-if="taskStatus.status === 'PENDING' || taskStatus.status === 'IN_PROGRESS'"
                               class="pi pi-spin pi-spinner" style="font-size: 1.5rem"></i>
                            <i v-else-if="taskStatus.status === 'COMPLETED'"
                               class="pi pi-check-circle" style="font-size: 1.5rem; color: green"></i>
                            <i v-else-if="taskStatus.status === 'FAILED'"
                               class="pi pi-times-circle" style="font-size: 1.5rem; color: red"></i>
                            <i v-else-if="taskStatus.status === 'CANCELLED'"
                               class="pi pi-ban" style="font-size: 1.5rem; color: orange"></i>

                            <span>{{ getTaskStatusMessage(taskStatus.status) }}</span>
                        </div>

                        <div v-if="taskStatus.status === 'COMPLETED'" class="studbook-info">
                            <span class="text-sm">
                                {{
                                    $t('message.filters.studbookInfo',{
                                        volume: taskStatus.volume,
                                        yearFrom: taskStatus.yearFrom,
                                        yearTo: taskStatus.yearTo,
                                        completedAt: taskStatus.completedAt,
                                    })
                                }}
                            </span>
                        </div>

                        <div v-if="taskStatus.status === 'FAILED' && taskStatus.errorMessage" class="error-info">
                            <span class="text-sm" style="color: red;">
                                {{ $t('message.filters.errorMessage') }}: {{ taskStatus.errorMessage }}
                            </span>
                        </div>

                        <CustomButton
                            v-if="taskStatus.status === 'PENDING' || taskStatus.status === 'IN_PROGRESS'"
                            :label="$t('message.filters.cancelGeneration')" secondary
                            @click="cancelStudbook"/>

                        <CustomButton
                            v-if="taskStatus.status === 'COMPLETED' && taskStatus.fileUrl"
                            :label="$t('message.filters.downloadStudbook')"
                            @click="downloadStudbook(taskStatus.fileUrl)"/>
                    </div>

                    <div v-if="previousCompletedTask && previousCompletedTask.fileUrl"
                         class="flex align-items-center gap-3">
                        <div class="studbook-info">
                            <span class="text-sm">
                                {{ $t('message.filters.previousStudbookAvailable') }}:
                                {{
                                    $t('message.filters.studbookInfo',{
                                        volume: previousCompletedTask.volume,
                                        yearFrom: previousCompletedTask.yearFrom,
                                        yearTo: previousCompletedTask.yearTo,
                                        completedAt: previousCompletedTask.completedAt,
                                    })
                                }}
                            </span>
                        </div>
                        <CustomButton
                            :label="$t('message.filters.downloadPreviousStudbook')"
                            @click="downloadStudbook(previousCompletedTask.fileUrl)"/>
                    </div>
                </div>
            </div>
            <div class="filters-view-inner flex gap-3 w-full flex-wrap">
                <div class="p-3 section-filters flex flex-column gap-3 justify-content-between">
                    <div>
                        <h2 class="section-header">{{ $t('message.filters.horseFilters') }}</h2>
                        <div class="section-inputs flex flex-column gap-3">
                            <div class="section-checkbox flex justify-content-end gap-3 mb-3">
                                <CustomCheckbox name="import"
                                                :label="$t('message.filters.inputs.import')"
                                                v-model="imported"/>
                                <CustomCheckbox name="export"
                                                :label="$t('message.filters.inputs.export')"
                                                v-model="exported"/>
                                <CustomCheckbox name="locally"
                                                :label="$t('message.filters.inputs.locallyBreed')"
                                                v-model="locally"/>
                            </div>
                            <CustomSelectOneHorseSex name="sex"
                                                     :label="$t('message.filters.inputs.sex')"
                                                     noPlaceholder v-model="horseReport.gender"
                                                     itemValue="value"/>
                            <CustomSelectOneCountry name="placeOfBirth"
                                                    :label="$t('message.filters.inputs.placeOfBirth')"
                                                    noPlaceholder
                                                    v-model="horseReport.countryOfBirthId"
                                                    itemValue="id" filter/>
                            <CustomCalendar name="year" :label="$t('message.filters.inputs.year')"
                                            view="year" noPlaceholder v-model="horseReport.years"
                                            dateFormat="yy"/>
                            <!-- <CustomSelectOneCountry v-if="imported" name="countryOfImport" :label="$t('message.filters.inputs.originCountry')" noPlaceholder
                            v-model="horseReport.countryIds" itemValue="id" filter /> -->
                            <CustomSelectOneCountry v-if="exported" name="countryOfExport"
                                                    :label="$t('message.filters.inputs.destinationCountry')"
                                                    noPlaceholder
                                                    v-model="horseReport.countryIds" itemValue="id"
                                                    filter/>
                            <CustomAutocompleteOwner name="breeder"
                                                     :label="$t('message.filters.inputs.breeder')"
                                                     noPlaceholder v-model="horseReport.breeders"/>
                            <CustomAutocompleteOwner name="owner"
                                                     :label="$t('message.filters.inputs.owner')"
                                                     noPlaceholder v-model="horseReport.owners"/>
                            <CustomAutocompleteHorse name="sire"
                                                     :label="$t('message.filters.inputs.sire')"
                                                     noPlaceholder v-model="horseReport.sires"/>
                            <CustomAutocompleteHorse name="dam"
                                                     :label="$t('message.filters.inputs.dam')"
                                                     noPlaceholder v-model="horseReport.dams"/>
                        </div>
                    </div>
                    <div class="flex justify-content-center w-full mt-3">
                        <CustomButton :label="$t('message.filters.generateButton')"
                                      @click="generateReport('horse')"/>
                    </div>
                </div>
                <div class="p-3 section-filters flex flex-column gap-3 justify-content-between">
                    <div>
                        <h2 class="section-header">{{ $t('message.filters.covering') }}</h2>
                        <div class="section-inputs flex flex-column gap-3">
                            <CustomSelectOneCity name="placeOfBirth"
                                                 :label="$t('message.filters.inputs.placeOfCovering')"
                                                 noPlaceholder
                                                 v-model="coveringReport.cities" itemValue="id"
                                                 filter/>
                            <CustomCalendar name="year" :label="$t('message.filters.inputs.season')"
                                            view="year" noPlaceholder v-model="coveringReport.years"
                                            dateFormat="yy"/>
                            <CustomSelectOneResultType name="result"
                                                       :label="$t('message.filters.inputs.result')"
                                                       noPlaceholder
                                                       v-model="coveringReport.results"/>
                            <CustomAutocompleteOwner name="breeder"
                                                     :label="$t('message.filters.inputs.breeder')"
                                                     noPlaceholder v-model="coveringReport.breeders"
                                                     searchParam="fullName"/>
                            <CustomAutocompleteOwner name="owner"
                                                     :label="$t('message.filters.inputs.owner')"
                                                     noPlaceholder v-model="coveringReport.owners"
                                                     searchParam="fullName"/>
                        </div>
                    </div>
                    <div class="flex justify-content-center w-full mt-3">
                        <CustomButton :label="$t('message.filters.generateButton')"
                                      @click="generateReport('covering')"/>
                    </div>
                </div>
                <div class="p-3 section-filters flex flex-column gap-3 justify-content-between">
                    <div>
                        <h2 class="section-header">{{ $t('message.filters.agreement') }}</h2>
                        <div class="section-inputs flex flex-column gap-3">
                            <CustomSelectOneCity name="location"
                                                 :label="$t('message.filters.inputs.placeOfAgreement')"
                                                 noPlaceholder
                                                 v-model="stallionsAgrReport.cities" itemValue="id"
                                                 filter/>
                            <CustomCalendar name="year" :label="$t('message.filters.inputs.season')"
                                            view="year" noPlaceholder
                                            v-model="stallionsAgrReport.years" dateFormat="yy"/>
                            <CustomSelectOneAgreementType name="agreementType"
                                                          :label="$t('message.filters.inputs.agreement')"
                                                          noPlaceholder
                                                          v-model="stallionsAgrReport.agreementTypes"/>
                            <CustomAutocompleteOwner name="breeder"
                                                     :label="$t('message.filters.inputs.breeder')"
                                                     noPlaceholder
                                                     v-model="stallionsAgrReport.breeders"
                                                     searchParam="fullName"/>
                            <CustomAutocompleteOwner name="owner"
                                                     :label="$t('message.filters.inputs.owner')"
                                                     noPlaceholder
                                                     v-model="stallionsAgrReport.owners"
                                                     searchParam="fullName"/>
                        </div>
                    </div>
                    <div class="flex justify-content-center w-full mt-3">
                        <CustomButton :label="$t('message.filters.generateButton')"
                                      @click="generateReport('stallionsAgr')"/>
                    </div>
                </div>
                <div class="p-3 section-filters flex flex-column gap-3 justify-content-between">
                    <div>
                        <h2 class="section-header">{{ $t('message.filters.broodmare') }}</h2>
                        <div class="section-inputs flex flex-column gap-3">
                            <CustomSelectOneCity name="placeOfBirth"
                                                 :label="$t('message.filters.inputs.placeOfBirth')"
                                                 noPlaceholder
                                                 v-model="broodmaresReport.cities" itemValue="id"
                                                 filter/>
                            <CustomCalendar name="year" :label="$t('message.filters.inputs.year')"
                                            view="year" noPlaceholder
                                            v-model="broodmaresReport.years" dateFormat="yy"/>
                            <CustomSelectOneHorseSex name="foalGender"
                                                     :label="$t('message.filters.inputs.foalGender')"
                                                     noPlaceholder v-model="broodmaresReport.gender"
                                                     itemValue="value"/>
                            <CustomAutocompleteOwner name="breeder"
                                                     :label="$t('message.filters.inputs.breeder')"
                                                     noPlaceholder
                                                     v-model="broodmaresReport.breeders"
                                                     searchParam="fullName"/>
                            <CustomAutocompleteOwner name="owner"
                                                     :label="$t('message.filters.inputs.owner')"
                                                     noPlaceholder v-model="broodmaresReport.owners"
                                                     searchParam="fullName"/>
                        </div>
                    </div>
                    <div class="flex justify-content-center w-full mt-3">
                        <CustomButton :label="$t('message.filters.generateButton')"
                                      @click="generateReport('broodmares')"/>
                    </div>
                </div>
                <div class="p-3 section-filters flex flex-column gap-3 justify-content-between">
                    <div>
                        <h2 class="section-header">{{ $t('message.filters.stallion') }}</h2>
                        <div class="section-inputs flex flex-column gap-3">
                            <CustomSelectOneCity name="placeOfBirth"
                                                 :label="$t('message.filters.inputs.placeOfBirth')"
                                                 noPlaceholder
                                                 v-model="stallionsReport.cities" itemValue="id"
                                                 filter/>
                            <CustomCalendar name="year" :label="$t('message.filters.inputs.year')"
                                            view="year" noPlaceholder
                                            v-model="stallionsReport.years" dateFormat="yy"/>
                            <CustomSelectOneHorseSex name="foalGender"
                                                     :label="$t('message.filters.inputs.foalGender')"
                                                     noPlaceholder v-model="stallionsReport.gender"
                                                     itemValue="value"/>
                            <CustomAutocompleteOwner name="breeder"
                                                     :label="$t('message.filters.inputs.breeder')"
                                                     noPlaceholder
                                                     v-model="stallionsReport.breeders"
                                                     searchParam="fullName"/>
                            <CustomAutocompleteOwner name="owner"
                                                     :label="$t('message.filters.inputs.owner')"
                                                     noPlaceholder v-model="stallionsReport.owners"
                                                     searchParam="fullName"/>
                        </div>
                    </div>
                    <div class="flex justify-content-center w-full mt-3">
                        <CustomButton :label="$t('message.filters.generateButton')"
                                      @click="generateReport('stallions')"/>
                    </div>
                </div>
                <div class="p-3 section-filters flex flex-column gap-3 justify-content-between">
                    <div>
                        <h2 class="section-header">{{ $t('message.filters.labFilters') }}</h2>
                        <div class="section-inputs flex flex-column gap-3">
                            <CustomSelectOneHorseSex name="sex"
                                                     :label="$t('message.filters.inputs.sex')"
                                                     noPlaceholder v-model="labReport.genders"
                                                     itemValue="value"/>
                            <CustomCalendar name="year" :label="$t('message.filters.inputs.year')"
                                            view="year" noPlaceholder v-model="labReport.years"
                                            dateFormat="yy"/>
                            <CustomSelectOneLaboratoryTestResult name="result"
                                                                 :label="$t('message.filters.inputs.result')"
                                                                 v-model="labReport.statuses"
                                                                 noPlaceholder/>
                        </div>
                    </div>
                    <div class="flex justify-content-center w-full mt-3">
                        <CustomButton :label="$t('message.filters.generateButton')"
                                      @click="generateReport('lab')"/>
                    </div>
                </div>
                <div class="p-3 section-filters flex flex-column gap-3 justify-content-between">
                    <div>
                        <h2 class="section-header">{{ $t('message.filters.deceased') }}</h2>
                        <div class="section-inputs flex flex-column gap-3">
                            <CustomSelectOneHorseSex name="sex"
                                                     :label="$t('message.filters.inputs.sex')"
                                                     noPlaceholder v-model="deceasedReport.gender"
                                                     itemValue="value"/>
                            <CustomSelectOneCountry name="placeOfBirth"
                                                    :label="$t('message.filters.inputs.placeOfBirth')"
                                                    noPlaceholder
                                                    v-model="deceasedReport.countryOfBirthId"
                                                    itemValue="id" filter/>
                            <CustomCalendar name="year" :label="$t('message.filters.inputs.year')"
                                            view="year" noPlaceholder v-model="deceasedReport.years"
                                            dateFormat="yy"/>
                            <CustomAutocompleteOwner name="breeder"
                                                     :label="$t('message.filters.inputs.breeder')"
                                                     noPlaceholder v-model="deceasedReport.breeders"
                                                     searchParam="fullName"/>
                            <CustomAutocompleteOwner name="owner"
                                                     :label="$t('message.filters.inputs.owner')"
                                                     noPlaceholder v-model="deceasedReport.owners"
                                                     searchParam="fullName"/>
                        </div>
                    </div>
                    <div class="flex justify-content-center w-full mt-3">
                        <CustomButton :label="$t('message.filters.generateButton')"
                                      @click="generateReport('deceased')"/>
                    </div>
                </div>
                <div class="p-3 section-filters flex flex-column gap-3 justify-content-between">
                    <div>
                        <h2 class="section-header">{{ $t('message.filters.owners') }}</h2>
                        <div class="section-inputs flex flex-column gap-3">
                            <CustomSelectOneHorseSex name="sex"
                                                     :label="$t('message.filters.inputs.sex')"
                                                     noPlaceholder v-model="ownersReport.gender"
                                                     itemValue="value"/>
                            <CustomSelectOneCity name="placeOfBirth"
                                                 :label="$t('message.filters.inputs.placeOfFarmStud')"
                                                 noPlaceholder
                                                 v-model="ownersReport.cities" itemValue="id"
                                                 filter/>
                            <CustomCalendar name="year" :label="$t('message.filters.inputs.year')"
                                            view="year" noPlaceholder v-model="ownersReport.years"
                                            dateFormat="yy"/>
                            <CustomAutocompleteOwner name="breeder"
                                                     :label="$t('message.filters.inputs.breeder')"
                                                     noPlaceholder v-model="ownersReport.breeders"
                                                     searchParam="fullName"/>
                            <CustomAutocompleteOwner name="owner"
                                                     :label="$t('message.filters.inputs.owner')"
                                                     noPlaceholder v-model="ownersReport.owners"
                                                     searchParam="fullName"/>
                            <CustomSelectOneFarm name="farms"
                                                 :label="$t('message.filters.inputs.farms')"
                                                 noPlaceholder
                                                 v-model="ownersReport.farms"></CustomSelectOneFarm>

                        </div>
                    </div>
                    <div class="flex justify-content-center w-full mt-3">
                        <CustomButton :label="$t('message.filters.generateButton')"
                                      @click="generateReport('owners')"/>
                    </div>
                </div>
                <div class="p-3 section-filters flex flex-column gap-3 justify-content-between">
                    <div>
                        <h2 class="section-header">{{ $t('message.filters.breeders') }}</h2>
                        <div class="section-inputs flex flex-column gap-3">
                            <CustomSelectOneHorseSex name="sex"
                                                     :label="$t('message.filters.inputs.sex')"
                                                     noPlaceholder v-model="breedersReport.gender"
                                                     itemValue="value"/>
                            <CustomCalendar name="year" :label="$t('message.filters.inputs.year')"
                                            view="year" noPlaceholder v-model="breedersReport.years"
                                            dateFormat="yy"/>
                            <CustomAutocompleteOwner name="breeder"
                                                     :label="$t('message.filters.inputs.breeder')"
                                                     noPlaceholder v-model="breedersReport.breeders"
                                                     searchParam="fullName"/>
                            <CustomSelectOneFarm name="farms"
                                                 :label="$t('message.filters.inputs.farms')"
                                                 noPlaceholder
                                                 v-model="breedersReport.farms"></CustomSelectOneFarm>

                        </div>
                    </div>
                    <div class="flex justify-content-center w-full mt-3">
                        <CustomButton :label="$t('message.filters.generateButton')"
                                      @click="generateReport('breeders')"/>
                    </div>
                </div>
                <!-- <div class="p-3 section-filters flex flex-column gap-3 justify-content-between">
                    <div>
                        <h2 class="section-header">{{ $t('message.filters.passport') }}</h2>
                        <div class="section-inputs flex flex-column gap-3">
                            <CustomSelectOneHorseSex name="sex" :label="$t('message.filters.inputs.sex')" noPlaceholder v-model="passportReport.gender" itemValue="value" />
                            <CustomCalendar name="year" :label="$t('message.filters.inputs.year')" view="year" noPlaceholder v-model="passportReport.years" dateFormat="yy" />
                            <CustomSelectOneCountry name="placeOfBirth" :label="$t('message.filters.inputs.placeOfBirth')" noPlaceholder
                                                    v-model="passportReport.cities" itemValue="id" filter />
                            <CustomAutocompleteOwner name="owner" :label="$t('message.filters.inputs.owner')" noPlaceholder v-model="passportReport.owners" searchParam="fullName" />
                        </div>
                    </div>
                    <div class="flex justify-content-center w-full mt-3">
                        <CustomButton :label="$t('message.filters.generateButton')" @click="generateReport('passport')"/>
                    </div>
                </div> -->
            </div>
        </div>
    </div>
</template>

<script>
import LibyanOrnament from "@/components/LibyanOrnament.vue";
import CustomCalendar from "@/components/form/CustomCalendar.vue";
import CustomSelectOneHorseSex from "@/components/form/CustomSelectOneHorseSex.vue";
import CustomButton from "@/components/form/CustomButton.vue";
import CustomCheckbox from "@/components/form/CustomCheckbox.vue";
import CustomSelectOneCountry from "@/components/form/CustomSelectOneCountry.vue";
import CustomAutocompleteOwner from "@/components/form/CustomAutocompleteOwner.vue";
import {FileUtils} from "@/utils/FileUtils";
import CustomSelectOneResultType from "@/components/form/CustomSelectOneResultType.vue";
import CustomSelectOneAgreementType from "@/components/form/CustomSelectOneAgreementType.vue";
import CustomAutocompleteHorse from "@/components/form/CustomAutocompleteHorse.vue";
import CustomSelectOneCity from "@/components/form/CustomSelectOneCity.vue";
import CustomSelectOneFarm from "@/components/form/CustomSelectOneFarm.vue";
import CustomSelectOneLaboratoryTestResult
    from "@/components/form/CustomSelectOneLaboratoryTestResult.vue";
import CustomInputNumber from "@/components/form/CustomInputNumber.vue";
import {ValidateUtils} from "@/utils/ValidateUtils";
import {cancelTask, generateStudbook, getTaskStatus} from "@/swagger/vue-api-client";

export default {
    name: "FiltersView",

    components: {
        CustomSelectOneLaboratoryTestResult,
        CustomSelectOneFarm,
        LibyanOrnament,
        CustomCalendar,
        CustomSelectOneHorseSex,
        CustomButton,
        CustomCheckbox,
        CustomSelectOneCountry,
        CustomAutocompleteOwner,
        CustomSelectOneResultType,
        CustomSelectOneAgreementType,
        CustomAutocompleteHorse,
        CustomSelectOneCity,
        CustomInputNumber,
    },

    data() {
        return {
            horseReport: {
                gender: null,
                countryOfBirthId: null,
                years: null,
                breeders: null,
                owners: null,
                countryIds: null,
                dams: null,
                sires: null,
                cities: null,
            },
            coveringReport: {
                cities: null,
                results: null,
                years: null,
                breeders: null,
                owners: null,
            },
            stallionsAgrReport: {
                gender: null,
                cities: null,
                years: null,
                breeders: null,
                owners: null,
                agreementTypes: null,
            },
            stallionsReport: {
                gender: null,
                cities: null,
                years: null,
                breeders: null,
                owners: null,
            },
            broodmaresReport: {
                gender: null,
                cities: null,
                years: null,
                breeders: null,
                owners: null,
            },
            labReport: {
                genders: null,
                years: null,
                statuses: null,
            },
            deceasedReport: {
                gender: null,
                countryOfBirthId: null,
                cities: null,
                years: null,
                breeders: null,
                owners: null,
            },
            ownersReport: {
                gender: null,
                years: null,
                breeders: null,
                owners: null,
                farms: null,
                cities: null,
            },
            breedersReport: {
                gender: null,
                farms: null,
                years: null,
                cities: null,
                owners: null,
            },
            passportReport: {
                gender: null,
                cities: null,
                years: null,
                owners: null,
            },
            owners: [],
            imported: false,
            exported: false,
            locally: false,
            studbook: {
                volume: null,
                yearFrom: null,
                yearTo: null,
            },
            showErrorMessages: false,
            taskStatus: null,
            previousCompletedTask: null,
            statusCheckInterval: null,
        };
    },

    mounted() {
        this.checkTaskStatus();
        this.statusCheckInterval = setInterval(() => {
            this.checkTaskStatus();
        }, 50000); // Check every 5 seconds
    },

    beforeUnmount() {
        if (this.statusCheckInterval) {
            clearInterval(this.statusCheckInterval);
        }
    },

    methods: {
        async generateStudbook() {
            if (!this.validateData()) {
                return;
            }

            try {
                const response = await generateStudbook({
                    volume: this.studbook.volume,
                    yearFrom: Number(this.studbook.yearFrom),
                    yearTo: Number(this.studbook.yearTo),
                });

                if (response.status === 202) {
                    this.taskStatus = response.data;
                    this.$toast.add({
                        severity: "info",
                        summary: this.$t("message.other.success"),
                        detail: this.$t("message.filters.studbookTaskStatus.inprogress"),
                        life: 3000,
                    });
                }
            } catch (error) {
                if (error.response && error.response.status === 409) {
                    this.$toast.add({
                        severity: "warn",
                        summary: this.$t("message.other.error"),
                        detail: this.$t("message.filters.generationInProgress"),
                        life: 5000,
                    });
                } else {
                    this.$toast.add({
                        severity: "error",
                        summary: this.$t("message.other.error"),
                        detail: error.response?.data || this.$t("message.other.unknownError"),
                        life: 5000,
                    });
                }
            }
        },

        async checkTaskStatus() {
            try {
                const response = await getTaskStatus();
                if (response.data) {
                    // Response always has the same structure: {currentTask, previousCompletedTask}
                    this.taskStatus = response.data.currentTask;
                    this.previousCompletedTask = response.data.previousCompletedTask;
                }
            } catch (error) {
                console.error("Error checking task status:", error);
            }
        },

        async cancelStudbook() {
            try {
                const response = await cancelTask();

                this.$toast.add({
                    severity: "success",
                    summary: this.$t("message.other.success"),
                    detail: response.data,
                    life: 3000,
                });

                // Refresh status after a short delay
                setTimeout(() => {
                    this.checkTaskStatus();
                }, 1000);
            } catch (error) {
                this.$toast.add({
                    severity: "error",
                    summary: this.$t("message.other.error"),
                    detail: error.response?.data || this.$t("message.other.unknownError"),
                    life: 5000,
                });
            }
        },

        downloadStudbook(fileUrl) {
            window.open(fileUrl, "_blank");
        },

        getTaskStatusMessage(status) {
            const statusKey = status.toLowerCase().replace("_", "");
            const messageKey = `message.filters.studbookTaskStatus.${statusKey}`;
            return this.$t(messageKey);
        },

        validateData() {
            let isCorrect = ValidateUtils.validateNotEmpty(this.studbook, ["volume", "yearFrom", "yearTo"]);
            isCorrect = this.studbook.yearTo && this.studbook.yearFrom && Number(this.studbook.yearTo) >= this.studbook.yearFrom && isCorrect;
            this.showErrorMessages = !isCorrect;
            return isCorrect;
        },

        getErrorMsg(value) {
            if (value && this.studbook.yearFrom && Number(value) < this.studbook.yearFrom) {
                return this.$t("message.validations.yearToEarlier");
            }
            return this.getRequiredMessage(value);
        },

        getRequiredMessage(value) {
            if (this.showErrorMessages && (value === null || value === "")) {
                return this.$t("message.validations.required");
            }
            return "";
        },

        generateReport(type) {
            if (type === "horse") {
                const horseReportRequest = {
                    gender: [],
                    cities: [],
                    years: [],
                    breeders: [],
                    owners: [],
                    countryIds: [],
                    dams: [],
                    sires: [],
                    countryOfBirthId: [],
                };
                if (this.horseReport.gender) horseReportRequest.gender.push(this.horseReport.gender);
                if (this.horseReport.countryOfBirthId) horseReportRequest.countryOfBirthId.push(this.horseReport.countryOfBirthId);
                if (this.horseReport.years) horseReportRequest.years.push(Number(this.horseReport.years));
                if (this.horseReport.breeders) horseReportRequest.breeders.push(this.horseReport.breeders.id);
                if (this.horseReport.owners) horseReportRequest.owners.push(this.horseReport.owners.id);
                if (this.horseReport.countryIds) horseReportRequest.countryIds.push(this.horseReport.countryIds);
                if (this.horseReport.dams) horseReportRequest.dams.push(this.horseReport.dams.id);
                if (this.horseReport.sires) horseReportRequest.sires.push(this.horseReport.sires.id);
                if (this.horseReport.cities) horseReportRequest.cities.push(this.horseReport.cities);
                if (this.imported) {
                    FileUtils.downloadFile(horseReportRequest, "horse_report.pdf", "/report/horse-imported");
                } else if (this.exported) {
                    FileUtils.downloadFile(horseReportRequest, "horse_report.pdf", "/report/horse-exported");
                } else if (this.locally) {
                    FileUtils.downloadFile(horseReportRequest, "horse_report.pdf", "/report/horse-local-breed");
                } else {
                    FileUtils.downloadFile(horseReportRequest, "horse_report.pdf", "/report/horse");
                }
            }
            if (type === "covering") {
                const coveringReportRequest = {
                    cities: [],
                    results: [],
                    years: [],
                    breeders: [],
                    owners: [],
                };
                if (this.coveringReport.cities) coveringReportRequest.cities.push(this.coveringReport.cities);
                if (this.coveringReport.results) coveringReportRequest.results.push(this.coveringReport.results);
                if (this.coveringReport.years) coveringReportRequest.years.push(this.coveringReport.years);
                if (this.coveringReport.breeders) coveringReportRequest.breeders.push(this.coveringReport.breeders.id);
                if (this.coveringReport.owners) coveringReportRequest.owners.push(this.coveringReport.owners.id);
                FileUtils.downloadFile(coveringReportRequest, "covering_report.pdf", "/report/covering");
            }
            if (type === "stallionsAgr") {
                const stallionsReportRequest = {
                    gender: [],
                    cities: [],
                    years: [],
                    breeders: [],
                    owners: [],
                    agreementTypes: [],
                };
                if (this.stallionsAgrReport.gender) stallionsReportRequest.gender.push(this.stallionsAgrReport.gender);
                if (this.stallionsAgrReport.cities) stallionsReportRequest.cities.push(this.stallionsAgrReport.cities);
                if (this.stallionsAgrReport.years) stallionsReportRequest.years.push(Number(this.stallionsAgrReport.years));
                if (this.stallionsAgrReport.breeders) stallionsReportRequest.breeders.push(this.stallionsAgrReport.breeders.id);
                if (this.stallionsAgrReport.owners) stallionsReportRequest.owners.push(this.stallionsAgrReport.owners.id);
                if (this.stallionsAgrReport.agreementTypes) stallionsReportRequest.agreementTypes.push(this.stallionsAgrReport.agreementTypes);
                FileUtils.downloadFile(stallionsReportRequest, "stallions_agreement_report.pdf", "/report/stallions-agreement");
            }
            if (type === "stallions") {
                const stallionsReportRequest = {
                    gender: [],
                    cities: [],
                    years: [],
                    breeders: [],
                    owners: [],
                };
                if (this.stallionsReport.gender) stallionsReportRequest.gender.push(this.stallionsReport.gender);
                if (this.stallionsReport.cities) stallionsReportRequest.cities.push(this.stallionsReport.cities);
                if (this.stallionsReport.years) stallionsReportRequest.years.push(Number(this.stallionsReport.years));
                if (this.stallionsReport.breeders) stallionsReportRequest.breeders.push(this.stallionsReport.breeders.id);
                if (this.stallionsReport.owners) stallionsReportRequest.owners.push(this.stallionsReport.owners.id);
                FileUtils.downloadFile(stallionsReportRequest, "stallions_progeny_report.pdf", "/report/stallions-progeny");
            }
            if (type === "broodmares") {
                const broodmaresReportRequest = {
                    gender: [],
                    cities: [],
                    years: [],
                    breeders: [],
                    owners: [],
                };
                if (this.broodmaresReport.gender) broodmaresReportRequest.gender.push(this.broodmaresReport.gender);
                if (this.broodmaresReport.cities) broodmaresReportRequest.cities.push(this.broodmaresReport.cities);
                if (this.broodmaresReport.years) broodmaresReportRequest.years.push(Number(this.broodmaresReport.years));
                if (this.broodmaresReport.breeders) broodmaresReportRequest.breeders.push(this.broodmaresReport.breeders.id);
                if (this.broodmaresReport.owners) broodmaresReportRequest.owners.push(this.broodmaresReport.owners.id);
                FileUtils.downloadFile(broodmaresReportRequest, "broodmares_produce_report.pdf", "/report/broodmares-produce");
            }
            if (type === "lab") {
                const labReportRequest = {
                    genders: [],
                    statuses: [],
                    years: [],
                };
                if (this.labReport.genders) labReportRequest.genders.push(this.labReport.genders);
                if (this.labReport.statuses) labReportRequest.statuses.push(this.labReport.statuses);
                if (this.labReport.years) labReportRequest.years.push(this.labReport.years);
                FileUtils.downloadFile(labReportRequest, "lab_report.pdf", "/report/lab");
            }
            if (type === "deceased") {
                const deceasedReportRequest = {
                    gender: [],
                    cities: [],
                    years: [],
                    breeders: [],
                    owners: [],
                    countryOfBirthId: [],
                };
                if (this.deceasedReport.gender) deceasedReportRequest.gender.push(this.deceasedReport.gender);
                if (this.deceasedReport.countryOfBirthId) deceasedReportRequest.countryOfBirthId.push(this.deceasedReport.countryOfBirthId);
                if (this.deceasedReport.cities) deceasedReportRequest.cities.push(this.deceasedReport.cities);
                if (this.deceasedReport.years) deceasedReportRequest.years.push(Number(this.deceasedReport.years));
                if (this.deceasedReport.breeders) deceasedReportRequest.breeders.push(this.deceasedReport.breeders.id);
                if (this.deceasedReport.owners) deceasedReportRequest.owners.push(this.deceasedReport.owners.id);
                FileUtils.downloadFile(deceasedReportRequest, "deceased_report.pdf", "/report/deceased");
            }
            if (type === "owners") {
                const ownersReportRequest = {
                    gender: [],
                    years: [],
                    breeders: [],
                    owners: [],
                    farms: [],
                    cities: [],
                };
                if (this.ownersReport.gender) ownersReportRequest.gender.push(this.ownersReport.gender);
                if (this.ownersReport.farms) ownersReportRequest.farms.push(this.ownersReport.farms);
                if (this.ownersReport.years) ownersReportRequest.years.push(Number(this.ownersReport.years));
                if (this.ownersReport.breeders) ownersReportRequest.breeders.push(this.ownersReport.breeders.id);
                if (this.ownersReport.owners) ownersReportRequest.owners.push(this.ownersReport.owners.id);
                if (this.ownersReport.cities) ownersReportRequest.cities.push(this.ownersReport.cities);

                FileUtils.downloadFile(ownersReportRequest, "owners_report.pdf", "/report/owners");
            }
            if (type === "breeders") {
                const breedersReportRequest = {
                    gender: [],
                    farms: [],
                    years: [],
                    breeders: [],
                    cities: [],
                };
                if (this.breedersReport.gender) breedersReportRequest.gender.push(this.breedersReport.gender);
                if (this.breedersReport.farms) breedersReportRequest.farms.push(this.breedersReport.farms);
                if (this.breedersReport.years) breedersReportRequest.years.push(Number(this.breedersReport.years));
                if (this.breedersReport.breeders) breedersReportRequest.breeders.push(this.breedersReport.breeders.id);
                if (this.breedersReport.cities) breedersReportRequest.cities.push(this.breedersReport.cities);

                FileUtils.downloadFile(breedersReportRequest, "breeders_report.pdf", "/report/breeders");
            }
        },
    },
    watch: {
        imported(value) {
            if (value) {
                this.exported = false;
                this.locally = false;
            }
        },
        exported(value) {
            if (value) {
                this.imported = false;
                this.locally = false;
            }
        },
        locally(value) {
            if (value) {
                this.exported = false;
                this.imported = false;
            }
        },
    },
};
</script>

<style lang="scss">
@import "../../assets/theme/mytheme/variables";

.filters-view-outer {
    .filters-view-inner {
        @media screen and (max-width: 1400px) {
            flex-wrap: wrap;
            justify-content: center;
            // .section-filters {
            //     flex: 1 1 0;
            // }
            .section-filters {
                width: 48% !important;
            }
        }
        @media screen and (max-width: 1000px) {
            .section-filters {
                width: 100% !important;
            }
        }
    }

    .section-filters {
        background-color: var(--secondary-color);
        flex: 1 1 calc(33.333% - 1.5rem);
        min-width: 452px;

        .section-header {
            font-family: $decorFontFamily;
            font-size: 25px;
            font-weight: 400;
            color: var(--header-color);
        }

        .section-checkbox {
            @media screen and (max-width: 420px) {
                flex-direction: column;
                align-items: flex-end;
            }
        }

        .section-inputs {
            .custom-input-text, .custom-input-number, .custom-select-one, .custom-calendar, .custom-multi-select, .custom-autocomplete {
                padding-left: 170px;

                .custom-label {
                    z-index: 999;
                    position: absolute;
                    transform: translateX(-100%);
                    left: 160px;
                    text-align: right;
                    max-width: 165px;
                }
            }

            .long-label .custom-label {
                width: 100px;
            }

            .custom-calendar {
                .p-calendar {
                    width: 100%;
                }
            }
        }
    }

    .studbook-generator {
        .custom-input-number, .custom-calendar {
            padding-left: 110px;

            .custom-label {
                z-index: 999;
                position: absolute;
                transform: translateX(-100%);
                left: 100px;
                text-align: right;
                max-width: 165px;
            }

            input {
                max-width: 200px;
            }
        }

        .long-label .custom-label {
            width: 100px;
        }

        .custom-calendar {
            .p-calendar {
                width: 100%;
            }
        }

        @media screen and (max-width: 1400px) {
            flex-wrap: wrap;
        }
    }
}
</style>
